import React, { useState, useEffect } from 'react';
import { FileText, Plus, X } from 'lucide-react';

interface Template {
  id: string;
  name: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplatePickerProps {
  onTemplateSelect: (content: string) => void;
  onTemplateCreate: (name: string, content: string) => Promise<void>;
  disabled?: boolean;
  className?: string;
  apiUrl: string;
  user: any;
}

export const TemplatePicker: React.FC<TemplatePickerProps> = ({
  onTemplateSelect,
  onTemplateCreate,
  disabled = false,
  className = "",
  apiUrl,
  user
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [creating, setCreating] = useState(false);

  // Fetch templates when component mounts
  useEffect(() => {
    if (isOpen && user) {
      fetchTemplates();
    }
  }, [isOpen, user]);

  const fetchTemplates = async () => {
    if (!user || !apiUrl) return;

    setLoading(true);
    try {
      // Get JWT token
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const jwtToken = session.tokens?.idToken?.toString();
      
      if (!jwtToken) {
        return;
      }

      const response = await fetch(`${apiUrl}/app/templates`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        // Optionally handle error silently or with UI feedback
      }
    } catch (error) {
      // Optionally handle error silently or with UI feedback
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateContent.trim()) return;

    setCreating(true);
    try {
      await onTemplateCreate(newTemplateName.trim(), newTemplateContent.trim());
      setNewTemplateName('');
      setNewTemplateContent('');
      setShowCreateForm(false);
      await fetchTemplates(); // Refresh templates
    } catch (error) {
      // Optionally handle error silently or with UI feedback
    } finally {
      setCreating(false);
    }
  };

  const handleTemplateSelect = (template: Template) => {
    onTemplateSelect(template.content);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-3 ${className}`}
        title="Use template"
      >
        <FileText className="h-4 w-4" />
      </button>
      
      {isOpen && (
        <div className="absolute bottom-full right-0 mb-2 z-50 w-80 sm:w-80 w-[calc(100vw-2rem)] bg-card border rounded-lg shadow-lg">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Message Templates</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="p-4 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading templates...
              </div>
            ) : showCreateForm ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Template Name</label>
                  <input
                    type="text"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="Enter template name"
                    className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Template Content</label>
                  <textarea
                    value={newTemplateContent}
                    onChange={(e) => setNewTemplateContent(e.target.value)}
                    placeholder="Enter template content"
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateTemplate}
                    disabled={creating || !newTemplateName.trim() || !newTemplateContent.trim()}
                    className="flex-1 inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-xs">
                    No templates yet
                  </div>
                ) : (
                  templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className="w-full text-left p-3 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <div className="font-medium text-sm">{template.name}</div>
                      <div className="text-xs text-muted-foreground mt-1 truncate">
                        {template.content}
                      </div>
                    </button>
                  ))
                )}
                
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Create Template
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 